import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Receipt, TriangleAlert } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { Card, ScrollFade } from '@app/components/core';
import { Skeleton } from '@ui/components/ui/skeleton';
import { hoursExact } from './portfolioHours';
import { ProductHours } from './productTime';

// Charged and not-charged are complements, so one at a time keeps the comparison honest.
type Scope = 'all' | 'charged' | 'notCharged';
// Widest first, then the two halves it splits into.
const SCOPES: Scope[] = ['all', 'charged', 'notCharged'];

const inScope = (product: ProductHours, scope: Scope): boolean => {
  if (scope === 'charged') return product.billable;
  if (scope === 'notCharged') return !product.billable;
  return true;
};

// Ranked hours per product. Non-billable means it never reaches a client invoice.
export const InvolvementsCard = ({
  products,
  isLoading,
  isError,
  periodLabel,
}: {
  products: ProductHours[];
  isLoading: boolean;
  /** Whether the read behind the figures failed. Takes precedence over an empty table. */
  isError: boolean;
  periodLabel: string;
}): ReactElement => {
  const { t } = useTranslation();
  // Not charged-by-default: two figures on one row that do not add up reads as an error.
  const [scope, setScope] = useState<Scope>('all');
  const [open, setOpen] = useState(false);

  const rows = useMemo(
    () =>
      products
        .filter(p => inScope(p, scope) && p.hours > 0)
        .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name)),
    [products, scope],
  );
  const grandTotal = rows.reduce((sum, p) => sum + p.hours, 0);

  const scopeLabel = (value: Scope): string =>
    ({ charged: t('involvementsCharged'), notCharged: t('involvementsNotCharged'), all: t('involvementsAll') })[value];

  return (
    <Card className="flex h-full flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Receipt className="size-4 text-muted-foreground" />
          {t('involvements')}
        </p>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            type="button"
            className="flex h-8 w-[10.5rem] items-center justify-between gap-2 rounded-lg border border-input bg-popover px-3 text-left text-sm text-foreground shadow-xs outline-none transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <span className="min-w-0 flex-1 truncate">{scopeLabel(scope)}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[10.5rem] p-1">
            {SCOPES.map(value => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setScope(value);
                  setOpen(false);
                }}
                className={cx(
                  'flex w-full items-center rounded-md px-2 py-1.5 text-sm text-foreground transition-colors',
                  scope === value ? 'bg-muted font-medium' : 'hover:bg-muted',
                )}
              >
                {scopeLabel(value)}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      {isLoading && <Skeleton className="h-64 w-full rounded-xl" />}

      {/* Never a clean-looking empty table on a failed read: no card on this page is allowed to report a
          result it does not have. */}
      {!isLoading && isError && (
        <p className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-destructive/40 px-4 py-6 text-center text-sm text-muted-foreground">
          <TriangleAlert className="size-5 text-destructive/70" aria-hidden />
          {t('cardReadFailed')}
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          {t('noInvolvementsInScope')}
        </p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="truncate text-xs font-medium text-foreground">{t('colProduct')}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{t('colHours')}</span>
          </div>
          <ScrollFade className="max-h-64 rounded-xl border border-border" fadeClassName="from-card rounded-b-xl">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {rows.map((p, i) => (
                  <tr key={p.id}>
                    {/* The rank makes the ordering explicit, the way the Data Studio tables number theirs. */}
                    <td className="w-8 py-1.5 pl-3 pr-0 text-right text-xs tabular-nums text-muted-foreground">
                      {i + 1}
                    </td>
                    <td className="truncate px-3 py-1.5 font-medium text-foreground">{p.name}</td>
                    <td className="w-24 px-3 py-1.5 text-right tabular-nums text-foreground">{hoursExact(p.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
          <div className="mt-2 flex items-baseline justify-between gap-2 px-3">
            <span className="text-xs font-medium text-foreground">{t('grandTotal')}</span>
            <span className="text-sm font-semibold tabular-nums text-foreground">{hoursExact(grandTotal)}</span>
          </div>
        </>
      )}

      {/* mt-auto, so a short table doesn't leave the caption stranded halfway up a card stretched to
          match the chart beside it. */}
      <p className="mt-auto pt-4 text-xs text-muted-foreground">{t('involvementsCaption', { period: periodLabel })}</p>
    </Card>
  );
};
